// Wake Forest University: public college page (#2652), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Wake Forest's Common Data Set 2025-2026 (CDS_2025-2026-PDF_Template_WFU.pdf,
//    on a wfu.edu file server), linked from Institutional
//    Research's Common Data Set page: C1 applicants, admits and enrollees for
//    Fall 2025 (5 applicants of unknown sex are in the total), C8 test policy
//    for Fall 2027, C21/C22 plans. The C1 residency table is blank, and the
//    C21 early decision counts are blank (the form still reads "For the Fall
//    2024 entering class"), so the page shows no early acceptance rate.
//  - admissions.wfu.edu: First-Year Students (the four application plans,
//    when decisions come, and the test-optional answer), Info for the Class
//    of 2031 (the dates and deadlines for students applying this fall for
//    fall 2027, and the application checklist), and Test Optional.
//  - financialaid.wfu.edu: Undergraduate Costs & Fees, 2026-27 Cost of
//    Attendance (living on campus).
//  - College Scorecard, school id 199847 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost departure: the CDS G1 gives 2026-2027 food as $10,530, which its own
// note says is the largest meal plan, with the typical resident's meals at
// $7,396. The page uses the financial aid office's cost of attendance, which
// uses $7,396 and otherwise matches the CDS (tuition $71,894, fees $1,276,
// housing $12,900, books $1,720, transportation $1,550, personal $1,930).
// That page's total, $98,730, adds a $64 average federal loan fee, which is
// left out here; the total is the sum of the rows.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Wake Forest Common Data Set 2025-2026',
        url: 'https://ir.wfu.edu/common-data-set/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Wake Forest Undergraduate Admissions, First-Year Students',
        url: 'https://admissions.wfu.edu/become-a-deacon/first-year/',
        read: '2026-10-01',
    },
    classOf: {
        label: 'Wake Forest Undergraduate Admissions, Info for the Class of 2031',
        url: 'https://admissions.wfu.edu/become-a-deacon/classof/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Wake Forest Undergraduate Admissions, Test Optional',
        url: 'https://admissions.wfu.edu/become-a-deacon/test-optional/',
        read: '2026-10-01',
    },
    coa: {
        label: 'Wake Forest Student Financial Aid, 2026-27 Cost of Attendance',
        url: 'https://financialaid.wfu.edu/cost-of-attendance/undergraduate/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?199847-Wake-Forest-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'wake-forest-university',
    kbId: 'wake_forest_university',
    name: 'Wake Forest University',
    shortName: 'Wake Forest',
    aliases: ['Wake', 'WFU'],
    location: 'Winston-Salem, NC',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Wake Forest brand colors); decoration only.
    branding: {
        primary: '#9E7E38',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/wake_forest_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 19600,
        admitted: 4068,
        enrolled: 1544,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Wake Forest does not admit by major, and its Common Data Set reports one first-year acceptance rate for the whole university.',
        source: 'firstYear',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'The Common Data Set leaves its early decision counts blank and gives no early action counts, so it gives no early acceptance rate. Wake Forest says it tends to enroll just over half of each incoming class through Early Decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 1' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 15', decision: 'Rolling', binding: true },
            { plan: 'Early Action (first-generation college students only)', deadline: 'November 15', decision: 'By January 15', binding: false },
            { plan: 'Early Decision II', deadline: 'January 1', decision: 'By February 15', binding: true },
            { plan: 'Regular Decision', deadline: 'January 1', decision: 'By April 1', binding: false },
        ],
        note: 'Merit- and talent-based scholarship applications are due November 15. An optional virtual interview can be requested within five days of applying, and no later than November 18, 2026; optional video interviews are due November 20 (Early Decision I and Early Action) or January 9 (Early Decision II and Regular Decision).',
        source: 'classOf',
        binding: 'Early Decision I and II are binding: if admitted, you have committed to being a Wake Forest student, and you send the Early Decision Agreement with the application. Early Action is open only to first-generation college students; it is not binding, and admitted students have until May 1 to decide.',
        bindingSource: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Wake Forest has been test-optional since 2008: neither the SAT nor the ACT is required. If you think your scores reflect your ability, send them; if not, don\'t, and you won\'t be penalized. International students whose first language is not English send a TOEFL, IELTS or Duolingo score.',
        source: 'tests',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 71894 },
            { item: 'Required fees', amount: 1276 },
            { item: 'Housing (on campus)', amount: 12900 },
            { item: 'Food (meal plan)', amount: 7396 },
            { item: 'Books and supplies', amount: 1720 },
            { item: 'Transportation', amount: 1550 },
            { item: 'Personal expenses', amount: 1930 },
        ],
        total: 98666,
        note: 'For an undergraduate living on campus. New students also pay a $380 orientation fee in their first semester.',
        source: 'coa',
        netPrice: {
            average: 28165,
            byIncome: [
                { income: '$0 to $30,000', amount: 6242 },
                { income: '$30,001 to $48,000', amount: 6576 },
                { income: '$48,001 to $75,000', amount: 14876 },
                { income: '$75,001 to $110,000', amount: 13571 },
                { income: 'Over $110,000', amount: 65247 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Wake Forest\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
